/**
 * Badge de profil — implémentation du plan README §8.
 *
 * Trois invariants :
 *   1. Le profil affiché vient UNIQUEMENT du BT-24 (`doc.profile`), jamais du XMP.
 *   2. La couleur n'est jamais le seul signal : texte + aria-label complet.
 *   3. Le mot « conforme » n'apparaît pas : on décrit un profil déclaré.
 */
import type { ExtractionWarning, InvoiceProfile, ProfileInfo } from '../lib/model';
import { PROFILE_BADGE_FR, PROFILE_LABELS_FR } from '../lib/labels.fr';
import { h } from './dom';

export type BadgeTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

export interface BadgeModel {
  text: string;
  tone: BadgeTone;
  tooltip: string;
  ariaLabel: string;
}

/** Textes d'aide, volontairement sans le mot « conforme ». */
const TOOLTIPS: Record<InvoiceProfile, string> = {
  MINIMUM: 'Profil minimal : 6 champs seulement, pas de lignes détaillées.',
  BASIC_WL: 'En-tête et totaux sans lignes détaillées.',
  BASIC: 'Lignes de facture incluses, sans les extensions EN 16931.',
  EN16931: 'Profil complet, norme européenne EN 16931.',
  EXTENDED: 'EN 16931 + extensions Factur-X.',
  XRECHNUNG: 'CIUS allemande d’EN 16931 (version lue dans le BT-24).',
  UNKNOWN: 'Profil non reconnu : lecture au mieux, certains champs peuvent manquer.',
};

export function badgeFor(profile: ProfileInfo): BadgeModel {
  const base = PROFILE_BADGE_FR[profile.profile];
  const withVersion = profile.profile === 'XRECHNUNG' && profile.version ? ` ${profile.version}` : '';
  const text = profile.profile === 'XRECHNUNG' ? `${base.short}${withVersion}` : base.short;

  const ariaLabel =
    profile.profile === 'UNKNOWN'
      ? 'Profil non reconnu : lecture au mieux'
      : `Profil ${PROFILE_LABELS_FR[profile.profile]}${withVersion} déclaré`;

  return {
    text,
    tone: base.tone,
    tooltip: profile.urn ? `${TOOLTIPS[profile.profile]}\nURN : ${profile.urn}` : TOOLTIPS[profile.profile],
    ariaLabel,
  };
}

/** Bandeau contextuel exigé par §8.2 et §8.3, rendu sous le badge. */
export function profileNotice(profile: ProfileInfo): string | null {
  if (profile.family === 'zugferd1') {
    return 'Format ZUGFeRD 1.0 : obsolète, non pris en charge — aucune lecture fiable possible.';
  }
  if (profile.profile !== 'UNKNOWN') return null;
  if (!profile.urn) {
    return 'Le vendeur n’a pas déclaré de profil (BT-24 absent). Seuls les champs présents sont affichés.';
  }
  return 'Le profil déclaré n’est pas reconnu : lecture au mieux, certains champs peuvent manquer.';
}

export interface ProfileMismatch {
  xmpLevel: string;
  bt24Profile: string;
}

/** Écart XMP ↔ BT-24 (avertissement de niveau « warn », pas « info »). */
export function profileMismatch(warnings: ExtractionWarning[], xmpLevel: string | null, bt24: string): ProfileMismatch | null {
  const hit = warnings.find((warning) => warning.code === 'PROFILE_MISMATCH' && warning.severity !== 'info');
  if (!hit) return null;
  return { xmpLevel: xmpLevel ?? 'inconnu', bt24Profile: bt24 };
}

/** Élément DOM du badge. `role="img"` + aria-label portent le sens complet. */
export function renderBadge(model: BadgeModel, extraClass = ''): HTMLElement {
  return h(
    'span',
    {
      class: `badge badge--${model.tone}${extraClass ? ` ${extraClass}` : ''}`,
      role: 'img',
      'aria-label': model.ariaLabel,
      title: model.tooltip,
      'data-tone': model.tone,
    },
    model.text,
  );
}
